// SHARED — keep in sync across pro/care/backbar/academy (canonical: workspace/shared-web-auth/). Extract to @rootegra/auth later.
//
// Types + helpers around the `app_my_memberships` RPC — the context-independent
// truth for the experience switcher. The RPC returns every hat the signed-in
// user holds, regardless of the x-active-role / x-active-salon headers.

import type { SupabaseClient } from '@supabase/supabase-js';

export type AccentTheme = 'cherry' | 'plum' | 'honey' | 'espresso' | 'verdigris';

/** Known staff roles; open-ended so new server-side roles never break the UI. */
export type StaffRole =
  | 'owner'
  | 'educator'
  | 'senior'
  | 'master'
  | 'admin'
  | 'stylist'
  | 'apprentice'
  | (string & {});

export type StaffMembership = {
  stylist_id: string;
  salon_id: string | null; // salonless staff exist (ordered after salon-ful ones)
  salon_name: string | null;
  salon_slug: string | null;
  accent_theme: AccentTheme | null;
  role: StaffRole;
  name: string | null;
  onboarding_completed: boolean;
  is_owner: boolean;
};

export type ClientMembership = {
  client_id: string;
  salon_id: string;
  salon_name: string | null;
  salon_slug: string | null;
  accent_theme: AccentTheme | null;
  name: string | null;
};

export type AdminMembership = {
  role: 'super_admin' | (string & {});
};

/** Shape of the app_my_memberships jsonb result. */
export type Memberships = {
  staff: StaffMembership[];
  clients: ClientMembership[];
  admin: AdminMembership | null;
};

/** A single hat, tagged for the switcher. */
export type Membership =
  | ({ kind: 'staff' } & StaffMembership)
  | ({ kind: 'client' } & ClientMembership)
  | ({ kind: 'admin' } & AdminMembership);

export const APP_URLS = {
  care: 'https://care.rootegra.com',
  pro: 'https://pro.rootegra.com',
  academy: 'https://academy.rootegra.com',
  backbar: 'https://backbar.rootegra.com',
} as const;

export type AppName = 'Care' | 'Pro' | 'Academy' | 'Backbar';

/** Defensive parse of the RPC's jsonb payload. */
export function parseMemberships(data: unknown): Memberships {
  const raw = (data ?? {}) as { staff?: unknown; clients?: unknown; admin?: unknown };
  return {
    staff: Array.isArray(raw.staff) ? (raw.staff as StaffMembership[]) : [],
    clients: Array.isArray(raw.clients) ? (raw.clients as ClientMembership[]) : [],
    admin:
      raw.admin && typeof raw.admin === 'object' ? (raw.admin as AdminMembership) : null,
  };
}

/**
 * Fetch every membership the signed-in user holds. Context-independent —
 * ignores x-active-role / x-active-salon. Throws on RPC error (callers decide
 * whether that is fatal; the switcher usually degrades to "no menu").
 */
export async function fetchMemberships(client: SupabaseClient): Promise<Memberships> {
  const { data, error } = await client.rpc('app_my_memberships');
  if (error) {
    throw new Error(`app_my_memberships failed: ${error.message}`);
  }
  return parseMemberships(data);
}

/** Flatten into switcher rows: staff (server order), then clients, then admin. */
export function flattenMemberships(m: Memberships): Membership[] {
  return [
    ...m.staff.map((s) => ({ kind: 'staff' as const, ...s })),
    ...m.clients.map((c) => ({ kind: 'client' as const, ...c })),
    ...(m.admin ? [{ kind: 'admin' as const, ...m.admin }] : []),
  ];
}

/** Stable identity for marking the current membership in the switcher. */
export function membershipId(m: Membership): string {
  switch (m.kind) {
    case 'staff':
      return `staff:${m.stylist_id}`;
    case 'client':
      return `client:${m.client_id}`;
    case 'admin':
      return 'admin';
  }
}

/** Which app this membership lives in. */
export function destinationFor(m: Membership): string {
  switch (m.kind) {
    case 'client':
      return APP_URLS.care;
    case 'staff':
      return m.role === 'owner' || m.role === 'educator' ? APP_URLS.pro : APP_URLS.academy;
    case 'admin':
      return APP_URLS.backbar;
  }
}

/** Human name of the destination app — for "Go to Care →" style copy. */
export function appNameFor(m: Membership): AppName {
  switch (destinationFor(m)) {
    case APP_URLS.care:
      return 'Care';
    case APP_URLS.pro:
      return 'Pro';
    case APP_URLS.academy:
      return 'Academy';
    default:
      return 'Backbar';
  }
}

function titleCase(s: string): string {
  return s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1);
}

/** e.g. 'Casa Cabello — Client', 'Nalo Studio — Owner', 'Rootegra — Admin'. */
export function labelFor(m: Membership): string {
  switch (m.kind) {
    case 'client':
      return `${m.salon_name ?? 'Rootegra'} — Client`;
    case 'staff':
      return `${m.salon_name ?? 'Rootegra'} — ${titleCase(m.role)}`;
    case 'admin':
      return 'Rootegra — Admin';
  }
}
